import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ArrowRight, FlaskConical, TrendingUp,
  Brain, Workflow, Blocks, Database, Bot, MapPin, Briefcase, Sparkles, CheckCircle2, Quote,
} from "lucide-react";

const categories = [
  { icon: Brain, label: "AI Analyst" },
  { icon: Bot, label: "Prompt Engineer" },
  { icon: Workflow, label: "AI Operations" },
  { icon: Database, label: "Data Roles" },
  { icon: FlaskConical, label: "ML Internships" },
  { icon: Blocks, label: "Automation Roles" },
];

const pillars = [
  { icon: Briefcase, title: "Get hired", desc: "Personalized job and internship matches across the AI economy — roles chosen for your skills, not keyword spam.", cta: "Find your role", to: "/jobs" },
  { icon: FlaskConical, title: "Earn with AI projects", desc: "Get paid to work on real AI projects — build, label, evaluate — and turn every brief into proof of work.", cta: "Browse projects", to: "/projects" },
  { icon: TrendingUp, title: "Grow your career", desc: "Ship projects, build a profile employers trust, and keep leveling up with every role you land.", cta: "Start your profile", to: "/register" },
];

const Section = ({ children, className = "" }) => (
  <section className={`px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24 lg:py-28 ${className}`}>{children}</section>
);

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">{children}</span>
);

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/projects?limit=3").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/jobs-teaser").then((r) => setJobs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pt-14 pb-12 lg:pt-20 lg:pb-20 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-20 items-center">
          <div>
            <motion.div initial="hidden" animate="show" variants={reveal} custom={0}>
              <div className="inline-flex items-center gap-2 bg-[#FDF0EC] border border-[#F8D7C4] rounded-full px-4 py-1.5">
                <span className="w-2 h-2 rounded-full bg-[#7A222B]" />
                <span className="text-xs font-semibold tracking-[0.15em] text-[#7A222B]">THE AI TALENT NETWORK</span>
              </div>
            </motion.div>
            <motion.h1
              initial="hidden" animate="show" variants={reveal} custom={1}
              className="font-display mt-7 text-4xl sm:text-5xl lg:text-[4.2rem] tracking-tight leading-[1.03] text-[#2D2825]"
              style={{ fontWeight: 800 }}
              data-testid="hero-headline"
            >
              Where AI work meets{" "}
              <span className="text-[#7A222B]">the people who can do it.</span>
            </motion.h1>
            <motion.p
              initial="hidden" animate="show" variants={reveal} custom={2}
              className="mt-6 text-base sm:text-lg text-[#6E665E] leading-relaxed max-w-xl"
            >
              Explore jobs, internships and paid AI projects matched to your skills — and get hired on the strength of your work, not your degree.
            </motion.p>
            <motion.div initial="hidden" animate="show" variants={reveal} custom={3} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Link
                to="/register"
                data-testid="hero-get-started-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white px-7 py-3.5 rounded-full font-semibold transition-colors shadow-lg shadow-[#7A222B]/20"
              >
                Explore opportunities <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/employers"
                data-testid="hero-explore-btn"
                className="font-semibold text-[#2D2825] underline underline-offset-4 decoration-[#D99B9F] hover:decoration-[#7A222B] hover:text-[#7A222B] transition-colors"
              >
                Hiring? Find AI talent
              </Link>
            </motion.div>
            <motion.div initial="hidden" animate="show" variants={reveal} custom={4} className="mt-10 pt-7 border-t border-[#EAE3DB] flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#6E665E]">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> <strong className="text-[#2D2825] font-semibold">1,200+</strong> candidates ready</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> <strong className="text-[#2D2825] font-semibold">80+</strong> hiring partners</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> Free to start</span>
            </motion.div>
          </div>

          {/* Proof-of-work profile card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-w-md w-full lg:justify-self-end"
          >
            <div className="absolute -top-5 -left-5 w-24 h-24 rounded-2xl bg-[#F8D7C4] hidden lg:block" />
            <div className="relative bg-white rounded-3xl border border-[#EAE3DB] shadow-xl shadow-[#2D2825]/10 p-6 sm:p-7" data-testid="hero-profile-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#2D2825] flex items-center justify-center shrink-0">
                    <span className="font-display text-[#F5EFE6] text-sm" style={{ fontWeight: 700 }}>PS</span>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2D2825]">Priya S.</p>
                    <p className="text-xs text-[#6E665E]">Data background · Bengaluru</p>
                  </div>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#E6F4EA] text-[#2E7D32] whitespace-nowrap">Open to roles</span>
              </div>

              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.15em] text-[#948A80]">Verified skills</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Python", "RAG pipelines", "Prompt evaluation", "Data labelling QA"].map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#F5EFE6] text-[#2D2825] px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3 text-[#2E7D32]" /> {s}
                  </span>
                ))}
              </div>

              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.15em] text-[#948A80]">Shipped projects</p>
              <div className="mt-3 space-y-2.5">
                {[
                  { title: "Kannada FAQ chatbot", meta: "RAG · LangChain · reviewed by mentor" },
                  { title: "Invoice field extractor", meta: "Vision model · eval report attached" },
                ].map((p) => (
                  <div key={p.title} className="flex items-center justify-between gap-3 border border-[#EAE3DB] rounded-xl px-4 py-3 hover:border-[#D99B9F] transition-colors">
                    <div>
                      <p className="text-sm font-semibold text-[#2D2825]">{p.title}</p>
                      <p className="text-xs text-[#948A80] mt-0.5">{p.meta}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#7A222B] -rotate-45 shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* New match notification */}
            <div className="relative -mt-4 ml-6 sm:ml-10 bg-[#2D2825] text-white rounded-2xl px-5 py-4 flex items-center gap-3.5 shadow-lg shadow-[#2D2825]/30" data-testid="hero-match-card">
              <div className="w-9 h-9 rounded-xl bg-[#7A222B] flex items-center justify-center shrink-0">
                <Sparkles className="w-4.5 h-4.5 text-[#F8D7C4]" />
              </div>
              <div>
                <p className="text-sm font-semibold">New match: AI Intern, Applied ML</p>
                <p className="text-xs text-[#C9C0B6]">Nexora Labs · matched on 3 verified skills</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES / TRUST */}
      <section className="bg-[#F5EFE6] border-y border-[#EAE3DB]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14">
          <p className="text-center text-sm text-[#948A80] font-medium">Jobs, internships & project work across the roles defining the AI economy</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((c) => (
              <div key={c.label} data-testid={`category-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="flex flex-col items-center gap-3 bg-white rounded-2xl border border-[#EAE3DB] py-6 px-3 hover:border-[#D99B9F] hover:shadow-md transition-all">
                <c.icon className="w-6 h-6 text-[#7A222B]" strokeWidth={1.8} />
                <span className="text-xs sm:text-sm font-medium text-[#2D2825] text-center">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <Section>
        <div className="max-w-2xl">
          <Overline>How Master works</Overline>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
            One network for jobs, internships & AI project work
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={reveal} custom={i}
              className="relative bg-white rounded-3xl border border-[#EAE3DB] p-8 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow flex flex-col"
            >
              <span className="font-mono text-sm text-[#D4A359]">0{i + 1}</span>
              <div className="mt-4 w-12 h-12 rounded-2xl bg-[#FDF0EC] flex items-center justify-center">
                <p.icon className="w-6 h-6 text-[#7A222B]" strokeWidth={1.8} />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-[#2D2825]">{p.title}</h3>
              <p className="mt-2 text-sm text-[#6E665E] leading-relaxed flex-1">{p.desc}</p>
              <Link to={p.to} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7A222B] hover:gap-2.5 transition-all">
                {p.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PROJECTS */}
      <Section>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Overline>Projects</Overline>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
              Get paid to work on real AI projects
            </h2>
          </div>
          <Link to="/projects" className="inline-flex items-center gap-2 text-[#7A222B] hover:text-[#5C1D24] font-medium transition-colors">
            Browse projects <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="mt-4 text-[#6E665E] max-w-2xl">Complete real briefs to earn income and turn every project into verified proof of your AI skills.</p>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {(projects.length ? projects : Array(3).fill(null)).map((p, i) => (
            <div key={p?.id || i} className="bg-white rounded-3xl border border-[#EAE3DB] p-7 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#FDF0EC] text-[#7A222B]">{p?.category || "AI"}</span>
                <span className="text-xs text-[#948A80]">{p?.difficulty}</span>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-[#2D2825]">{p?.title || "AI Project"}</h3>
              <p className="mt-2 text-sm text-[#6E665E] leading-relaxed line-clamp-3">{p?.brief}</p>
              <p className="mt-4 text-xs font-mono text-[#D4A359]">{p?.duration}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* JOBS */}
      <section className="bg-[#FDF0EC] border-y border-[#F8D7C4]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <Overline>Jobs</Overline>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
                AI job reccos matched to your skills
              </h2>
            </div>
            <Link to="/jobs" className="inline-flex items-center gap-2 text-[#7A222B] hover:text-[#5C1D24] font-medium transition-colors">
              See all roles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="opportunity-types">
            {["Full-time", "Part-time", "Internships", "Remote"].map((t) => (
              <Link
                key={t}
                to="/jobs"
                data-testid={`opp-type-${t.toLowerCase()}`}
                className="text-sm font-medium bg-white border border-[#EAE3DB] text-[#2D2825] px-5 py-2 rounded-full hover:border-[#7A222B] hover:text-[#7A222B] transition-colors"
              >
                {t}
              </Link>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {(jobs.length ? jobs : Array(3).fill(null)).map((j, i) => (
              <div key={j?.id || i} className="bg-white rounded-3xl border border-[#EAE3DB] p-7 hover:shadow-xl transition-shadow">
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F5EFE6] text-[#7A222B]">{j?.job_type || "Role"}</span>
                <h3 className="mt-4 text-lg font-semibold text-[#2D2825]">{j?.title || "AI Role"}</h3>
                <p className="mt-1 text-sm text-[#6E665E]">{j?.company}</p>
                <div className="mt-4 flex items-center gap-4 text-xs text-[#948A80]">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j?.location}</span>
                  <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j?.work_mode}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-[#948A80]">Sign up to unlock the full job board — jobs, internships and projects — and apply in one click.</p>
        </div>
      </section>

      {/* WHY */}
      <Section>
        <div id="employers" className="scroll-mt-24 grid lg:grid-cols-2 gap-10">
          <div className="bg-white rounded-3xl border border-[#EAE3DB] p-8 sm:p-10">
            <Overline>Why candidates choose Master</Overline>
            <ul className="mt-6 space-y-5">
              {[
                { title: "Get personalized job reccos", desc: "Jobs and internships matched to your verified skills and shipped projects — never keyword spam." },
                { title: "Earn through AI projects", desc: "Paid briefs and freelance gigs that build real income and real proof of work." },
                { title: "Grow with proof of work", desc: "Every project you ship becomes a verified signal employers trust — beyond the résumé." },
              ].map((t) => (
                <li key={t.title} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#7A222B] shrink-0 mt-0.5" />
                  <span className="text-[#2D2825]"><strong className="font-semibold">{t.title}.</strong> <span className="text-[#6E665E]">{t.desc}</span></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#F5EFE6] rounded-3xl border border-[#EAE3DB] p-8 sm:p-10">
            <Overline>Why employers use it</Overline>
            <p className="mt-6 text-lg text-[#2D2825] leading-relaxed">
              Access candidates already working and building in AI — with projects that prove skills beyond a résumé.
            </p>
            <p className="mt-4 text-sm text-[#948A80]">Employer self-serve hiring is coming soon. Today, our team curates every role.</p>
          </div>
        </div>
      </Section>

      {/* CANDIDATE STORIES */}
      <Section>
        <div className="max-w-2xl">
          <Overline>Candidate stories</Overline>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
            What candidates say about Master
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-[#EAE3DB] rounded-3xl p-8 sm:p-10 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow" data-testid="candidate-story-1">
            <Quote className="w-8 h-8 text-[#D4A359]" />
            <p className="mt-5 font-display text-lg sm:text-xl leading-relaxed text-[#2D2825]" style={{ fontWeight: 500 }}>
              "I applied with two shipped projects instead of a CV full of buzzwords. Three days later I had my AI internship offer — the team said my project work is what made the decision easy."
            </p>
            <p className="mt-6 text-sm font-semibold text-[#2D2825]">Ananya R.</p>
            <p className="text-sm text-[#948A80]">AI Intern @ Nexora Labs</p>
          </div>
          <div className="bg-white border border-[#EAE3DB] rounded-3xl p-8 sm:p-10 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow" data-testid="candidate-story-2">
            <Quote className="w-8 h-8 text-[#D4A359]" />
            <p className="mt-5 font-display text-lg sm:text-xl leading-relaxed text-[#2D2825]" style={{ fontWeight: 500 }}>
              "The paid project briefs were the best training I've had — real briefs, real feedback, real money. By the time I interviewed, I wasn't describing skills, I was showing them."
            </p>
            <p className="mt-6 text-sm font-semibold text-[#2D2825]">Rohit M.</p>
            <p className="text-sm text-[#948A80]">Prompt Engineer · started with Master projects</p>
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#7A222B] text-white px-8 py-16 sm:px-16 sm:py-20 text-center">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#8f2f38]" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#651d24]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontWeight: 700 }}>
              Join to find AI jobs, internships & paid projects
            </h2>
            <p className="mt-5 text-[#F8D7C4] max-w-xl mx-auto">
              One network where your work gets you discovered — for roles across the AI economy.
            </p>
            <Link
              to="/register"
              data-testid="final-cta-btn"
              className="mt-9 inline-flex items-center gap-2 bg-white text-[#7A222B] px-8 py-4 rounded-full font-semibold hover:bg-[#FDF0EC] transition-colors"
            >
              Create your free account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
